import { useCallback, useMemo } from 'react';
import { Button, Chip, Stack, Typography, Paper, Box, CircularProgress } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { useConfirm, useAlert } from '../../components/dialogs/DialogProvider';
import { fmtOrEmDash } from '../../utils/format';
import type { ApiError } from '../../api/client';
import type { CaGenerationStatusRow, StillOnV1Sc } from '../../api/types';
import {
  useCaStatus,
  useMyPermissions,
  useRotateOperationalCa,
  useRotateCommissioningCa,
  useRetireCommissioningCa,
  useForceOsUpgrade,
} from '../../api/queries';

/** Operator+platform: view + operational rotate. */
export const SSH_PERMISSION = 'provisioning.site-controllers.ssh';
/** Platform-only: commissioning rotate + retire. */
export const COMMISSIONING_CA_PERMISSION = 'provisioning.commissioning-ca.retire';

type GenRow = CaGenerationStatusRow & { id: string };
type HoldoutRow = StillOnV1Sc & { id: string };

/**
 * SC SSH Admin Access & Recovery — Phase 2 (Task C2). CA rotation control
 * surface. Shows per-generation device counts for both CA kinds, the "still on
 * v1" in-service SC list (label + operator, never raw GUIDs), and the rotate /
 * retire / force-os-upgrade actions. The mutating commissioning actions are
 * Platform-only (commissioning-ca.retire); the page itself + operational rotate
 * are operator+platform (…ssh). Action buttons are hidden from non-holders.
 */
export default function CaRotationPage() {
  usePageTitle('CA rotation');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const canView = has(SSH_PERMISSION) || has(COMMISSIONING_CA_PERMISSION);
  const canManageCommissioning = has(COMMISSIONING_CA_PERMISSION);
  const canRotateOperational = has(SSH_PERMISSION);

  const status = useCaStatus(canView);
  const rotateOp = useRotateOperationalCa();
  const rotateComm = useRotateCommissioningCa();
  const retireComm = useRetireCommissioningCa();
  const forceOs = useForceOsUpgrade();
  const confirm = useConfirm();
  const alert = useAlert();

  const latestCommGen = status.data?.latestCommissioningGeneration ?? 1;

  const genRows = useMemo<GenRow[]>(
    () => (status.data?.generations ?? []).map((g) => ({ ...g, id: `${g.kind}-${g.generation}` })),
    [status.data?.generations],
  );
  const holdoutRows = useMemo<HoldoutRow[]>(
    () => (status.data?.stillOnV1 ?? []).map((s) => ({ ...s, id: s.siteControllerId })),
    [status.data?.stillOnV1],
  );

  const onError = useCallback((title: string, e: unknown) => {
    const err = e as unknown as ApiError;
    const msg = err?.status === 404
      ? 'That action endpoint is not available yet (Task D). Try again after the next deploy.'
      : err?.message ?? 'The action did not complete.';
    alert({ title, message: msg, severity: 'error' });
  }, [alert]);

  const genColumns = useMemo<GridColDef<GenRow>[]>(() => [
    { field: 'kind', headerName: 'CA kind', width: 150 },
    { field: 'generation', headerName: 'Generation', width: 120, type: 'number', align: 'right', headerAlign: 'right' },
    {
      field: 'isCurrent', headerName: 'Current', width: 120,
      renderCell: (p) => p.row.isCurrent
        ? <Chip size="small" label="current" color="success" />
        : <Chip size="small" label={p.row.state} variant="outlined" />,
    },
    {
      field: 'deviceCount', headerName: 'Devices on this gen', width: 180,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => p.row.deviceCount === null || p.row.deviceCount === undefined
        ? <Typography variant="body2" color="text.secondary">—</Typography>
        : <span>{p.row.deviceCount}</span>,
    },
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false, filterable: false,
      renderCell: (p) => {
        // Retire is only valid for a non-current commissioning generation.
        if (p.row.kind !== 'commissioning' || p.row.isCurrent || p.row.generation <= 1) return null;
        if (!canManageCommissioning) return null;
        return (
          <Button size="small" color="error" onClick={() => void runRetire(p.row.generation)}>
            Retire
          </Button>
        );
      },
    },
  ], [canManageCommissioning]); // eslint-disable-line react-hooks/exhaustive-deps

  const holdoutColumns = useMemo<GridColDef<HoldoutRow>[]>(() => [
    {
      field: 'label', headerName: 'Site controller', flex: 1.2,
      renderCell: (p) => p.row.label
        ? <code style={{ fontSize: 11 }}>{p.row.label}</code>
        : <Typography variant="body2" color="text.secondary">— (no QR Code)</Typography>,
    },
    {
      field: 'operatorName', headerName: 'Operator', flex: 1,
      renderCell: (p) => <span>{fmtOrEmDash(p.row.operatorName)}</span>,
    },
    {
      field: 'trustedCommissioningGens', headerName: 'Reported gens', width: 160,
      renderCell: (p) => p.row.trustedCommissioningGens && p.row.trustedCommissioningGens.length > 0
        ? <span>{p.row.trustedCommissioningGens.join(', ')}</span>
        : <Typography variant="body2" color="text.secondary">— (not reported)</Typography>,
    },
    {
      field: 'actions', headerName: 'Force OS upgrade', width: 180, sortable: false, filterable: false,
      renderCell: (p) => {
        if (!canManageCommissioning) return null;
        return (
          <Button size="small" onClick={() => void runForceOs(p.row)}>
            Force OS upgrade
          </Button>
        );
      },
    },
  ], [canManageCommissioning]); // eslint-disable-line react-hooks/exhaustive-deps

  const runRotateOp = useCallback(async () => {
    const ok = await confirm({
      title: 'Rotate operational CA',
      message: 'Generate a new operational SSH CA generation and make it current. Both the old + new CA stay trusted (7-day overlap) until you finalize-retire the old one.',
      confirmLabel: 'Rotate',
    });
    if (!ok) return;
    rotateOp.mutate(undefined, { onError: (e) => onError('Rotate failed', e) });
  }, [confirm, rotateOp, onError]);

  const runRotateComm = useCallback(async () => {
    const ok = await confirm({
      title: 'Rotate commissioning CA',
      message: 'Generate a new commissioning SSH CA generation and push it to the fleet (dual-trust with the baked anchor). In-service SCs pick it up on their next reconcile; the "still on v1" list tracks uptake.',
      confirmLabel: 'Rotate',
    });
    if (!ok) return;
    rotateComm.mutate(undefined, { onError: (e) => onError('Rotate failed', e) });
  }, [confirm, rotateComm, onError]);

  const runRetire = useCallback(async (generation: number) => {
    const holdouts = holdoutRows.length;
    const ok = await confirm({
      title: `Retire commissioning generation ${generation}`,
      message: holdouts > 0
        ? `${holdouts} in-service SC(s) have NOT yet reconciled the latest generation. Retiring now is blocked unless you force it — forced holdouts will need a reflash to recover commissioning trust.`
        : 'Every in-service SC has reconciled the latest generation. Retiring marks this generation destroyed (no automatic key destruction).',
      confirmLabel: holdouts > 0 ? 'Force retire' : 'Retire',
      destructive: true,
    });
    if (!ok) return;
    retireComm.mutate(
      { generation, force: holdouts > 0 },
      {
        onError: (e) => {
          const err = e as unknown as ApiError;
          if (err?.status === 409 || err?.code === 'uptake_incomplete') {
            alert({
              title: 'Retire blocked',
              message: 'In-service SCs have not reconciled this generation. Use the force option to retire anyway (holdouts need reflash).',
              severity: 'warning',
            });
            return;
          }
          onError('Retire failed', e);
        },
      },
    );
  }, [confirm, retireComm, holdoutRows.length, alert, onError]);

  const runForceOs = useCallback(async (row: HoldoutRow) => {
    const ok = await confirm({
      title: 'Force OS upgrade',
      message: (
        <>
          Force an A/B OS upgrade on{' '}
          <strong>{row.label ?? row.siteControllerId}</strong>
          {row.operatorName ? <> ({row.operatorName})</> : null}? This nudges a
          holdout that hasn't reconciled the latest commissioning CA.
        </>
      ),
      confirmLabel: 'Force upgrade',
    });
    if (!ok) return;
    forceOs.mutate({ scId: row.siteControllerId }, { onError: (e) => onError('Force OS upgrade failed', e) });
  }, [confirm, forceOs, onError]);

  if (!canView) {
    return (
      <Typography color="text.secondary">
        You don't have permission to view SSH CA rotation status.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4">CA rotation</Typography>
        <Typography variant="body2" color="text.secondary">
          SC SSH Admin Access &amp; Recovery — Phase 2. Rotate the operational and
          commissioning SSH CAs, watch fleet uptake, and retire an old commissioning
          generation once every in-service SC has reconciled the new one. Commissioning
          rotate + retire are ARMS-Staff (Platform) only.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2}>
        {canRotateOperational && (
          <Button variant="outlined" onClick={() => void runRotateOp()} disabled={rotateOp.isPending}>
            Rotate operational CA
          </Button>
        )}
        {canManageCommissioning && (
          <Button variant="contained" onClick={() => void runRotateComm()} disabled={rotateComm.isPending}>
            Rotate commissioning CA
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Generations</Typography>
        {status.isLoading
          ? <CircularProgress size={24} />
          : (
            <DataGrid<GenRow>
              rows={genRows}
              columns={genColumns}
              autoHeight
              density="compact"
              hideFooter
              disableRowSelectionOnClick
            />
          )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Still on v1{' '}
          <Chip
            size="small"
            color={holdoutRows.length > 0 ? 'warning' : 'success'}
            label={`${holdoutRows.length} device${holdoutRows.length === 1 ? '' : 's'}`}
          />
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          In-service site controllers that have NOT yet reconciled commissioning
          generation {latestCommGen} (the latest). A device that has never reported its
          trusted set counts as a holdout. Retiring the previous generation is blocked
          while any remain — unless forced (those then need reflash).
        </Typography>
        {status.isLoading
          ? <CircularProgress size={24} />
          : (
            <DataGrid<HoldoutRow>
              rows={holdoutRows}
              columns={holdoutColumns}
              autoHeight
              density="compact"
              hideFooter
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: 'No holdouts — every in-service SC is current.' }}
            />
          )}
      </Paper>
    </Stack>
  );
}
